import { useEffect, useMemo } from 'react';
import store from 'storejs';
import { isEmpty } from 'lodash';

import { useGetExplorerCurrentIp } from '@src/emp-3.0/api/queryHooks';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { EQueryIdType } from '@src/emp-3.0/types';
import { useExplorerIdStore } from '@src/pages/livelens/store';

export const EXPLORER_HISTORY_IPS = 'explorerHistoryIPs';
export const EXPLORER_HISTORY_USER_IDS = 'explorerHistoryUserIds';
export const EXPLORER_HISTORY_CLIENT_IDS = 'explorerHistoryClientIds';

export const idOptions = [
  {
    label: 'Find Myself By IP Address',
    value: EQueryIdType.ip,
    placeholder: 'Select or paste an IP',
  },
  {
    label: 'Find Myself By User ID',
    value: EQueryIdType.userId,
    placeholder: 'Select or paste a User ID',
  },
  {
    label: 'Find Myself By Client ID',
    value: EQueryIdType.clientId,
    placeholder: 'Select or paste a Client ID',
  },
];

// Per-customer gate for the "Find Myself By IP Address" option in LiveLens.
// Flag arrives via the profile payload as `enable_livelens_find_by_ip`
// (emp-server default = true). We treat only an explicit `false` as opt-out
// so that a missing/undefined value preserves current behaviour.
// See CE-11804.
export const useLivelensFindByIpEnabled = (): boolean => {
  const enabled = useInitialInfoStore((state) => state.userInfo?.enable_livelens_find_by_ip);
  return enabled !== false;
};

const isIpType = (titleValue: EQueryIdType): boolean => titleValue === EQueryIdType.ip;

export const useIds = () => {
  const { titleValue, setTitleValue, selectedId, setSelectedId, setDefaultIpvValue } =
    useExplorerIdStore();
  const findByIpEnabled = useLivelensFindByIpEnabled();

  const visibleIdOptions = useMemo(
    () =>
      findByIpEnabled ? idOptions : idOptions.filter((opt) => opt.value !== EQueryIdType.ip),
    [findByIpEnabled],
  );

  // Store default is EQueryIdType.ip; if this customer has the option hidden,
  // coerce off IP once so the dropdown doesn't render an orphan selection.
  useEffect(() => {
    if (!findByIpEnabled && titleValue === EQueryIdType.ip) {
      setTitleValue(EQueryIdType.userId);
    }
  }, [findByIpEnabled, titleValue, setTitleValue]);

  const { data: curIpv4, isFetching: ipv4ListLoading } = useGetExplorerCurrentIp(null, {
    enable: isEmpty(selectedId),
  });
  const { data: curIpv6, isFetching: ipv6ListLoading } = useGetExplorerCurrentIp(
    {
      baseURL: BUILD_VARS.IPV6_DOMAIN,
    },
    {
      enable: isEmpty(selectedId),
    },
  );
  const ips = store.get(EXPLORER_HISTORY_IPS);
  const users = store.get(EXPLORER_HISTORY_USER_IDS);
  const clients = store.get(EXPLORER_HISTORY_CLIENT_IDS);

  const isIpSelected = isIpType(titleValue);
  const isIpListLoading = ipv4ListLoading || ipv6ListLoading;

  const currentIps = useMemo(() => {
    const ips: string[] = [];
    if (curIpv4?.value) ips.push(curIpv4.value);
    if (curIpv6?.value) ips.push(curIpv6.value);
    return ips;
  }, [curIpv4, curIpv6]);

  const defaultIpvValue = useMemo(() => {
    return currentIps.map((ip) => `${ip}(Current)`);
  }, [currentIps]);

  useEffect(() => {
    if (isEmpty(defaultIpvValue) || !isEmpty(selectedId)) {
      return;
    }
    setDefaultIpvValue(defaultIpvValue);
    setSelectedId(defaultIpvValue);
  }, [defaultIpvValue]);

  const ipOptions = useMemo(() => {
    if (isIpListLoading) return [];
    let historyIPs = ips?.split(',') || [];
    historyIPs = historyIPs.filter((ip) => !currentIps.includes(ip));

    return [
      ...defaultIpvValue.map((ip) => ({ label: ip, value: ip })),
      ...historyIPs.map((ip) => ({
        label: ip,
        value: ip,
      })),
    ];
  }, [defaultIpvValue, isIpListLoading, ips]);

  const getUserIdOptions = () => {
    const historys = users?.split(',') || [];
    return (
      historys.map((userId) => ({
        label: userId,
        value: userId,
      })) || []
    );
  };

  const getClientIdOptions = () => {
    const historys = clients?.split(',') || [];
    return (
      historys.map((id) => ({
        label: id,
        value: id,
      })) || []
    );
  };

  const valueOptions = {
    [EQueryIdType.ip]: ipOptions,
    [EQueryIdType.userId]: getUserIdOptions(),
    [EQueryIdType.clientId]: getClientIdOptions(),
  };

  const handleIdSelectChanged = (value) => {
    const isIp = isIpType(value);
    if (isIp) {
      setSelectedId(defaultIpvValue);
    } else {
      const defaultSelectedId = valueOptions[value]?.[0]?.value;
      setSelectedId(defaultSelectedId != null ? [defaultSelectedId] : []);
    }
    setTitleValue(value);
  };

  return {
    titleValue,
    selectedId,
    idGroupData: [
      {
        titleValue: titleValue,
        titleOptions: visibleIdOptions,
        titleOnChange: handleIdSelectChanged,
        value: selectedId,
        options: valueOptions[titleValue],
        type: isIpSelected ? 'select' : 'auto-complete',
        mode: isIpSelected ? 'tags' : undefined,
        style: { maxWidth: 'none', minWidth: 200 },
        autoCompleteWidth: 240,
        placeholder: visibleIdOptions.find((item) => item.value === titleValue)?.placeholder,
        maxTagCount: 'responsive',
        onChange: (value) => {
          const newValue = value && value !== '' ? (Array.isArray(value) ? value : [value]) : [];
          setSelectedId(newValue);
          //   setFilterDraft({ ...filterDraft, user_id: value });
        },
        onSelect: (value) => {
          //   autoFillUserId(value);
        },
        onClear: () => {
          //   setFilterDraft({ ...filterDraft, user_id: '' });
        },
      },
      //   {
      //     titleValue: 'Client ID',
      //     titleDisabled: true,
      //     value: client_id,
      //     options: clientIdOptions,
      //     type: 'auto-complete',
      //     onSelect: (value) => {
      //       autoFillClientId(value);
      //     },
      //     onClear: () => {
      //       setFilterDraft({ ...filterDraft, client_id: '' });
      //     },
      //     onChange: (value) => {
      //       console.log('Client ID===', value);
      //       setFilterDraft({ ...filterDraft, client_id: value });
      //     },
      //   },
      // {
      //   titleValue: 'Device',
      //   titleDisabled: true,
      //   value: device_name,
      //   disabled: !!user_id && !!client_id,
      //   options: [{ label: device_name, value: device_name }],
      //   onChange: () => { },
      // },
      // {
      //   titleValue: 'App Ver',
      //   titleDisabled: true,
      //   value: application_version,
      //   disabled: !!user_id && !!client_id,
      //   options: [{ label: application_version, value: application_version }],
      //   onChange: () => { },
      // },
    ],
    defaultIpvValue,
  };
};
